import { Injectable } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import moment from "moment";

import { BehaviorSubject, Observable, Subject, observable, of, throwError } from "rxjs";
import { catchError, distinctUntilChanged, filter, map, take, tap } from "rxjs/operators";
import { IAnalysisSummary, IAnalysis, UIAnalysisModelType } from "src/app/_interfaces/IAnalysis";

import { ILandscapeJobInformation } from "src/app/_interfaces/ILandscapeJobInformation";
import { WfdssCase } from "src/app/_models/incident/wfdssCase/wfdssCase";
import { IrwinSearchService } from "../incident/irwin/irwin-search.service";
import { AnalysisDataService, EFsProModelOutputType } from "./analysis-data.service";
import { AnalysisModelTypes, IUIAnalysisModel } from "../../_interfaces/IAnalysis";
import { IFsProWeather } from "src/app/_interfaces/IFmsf";
import { ICompleteLSJobSummary } from "src/app/_components/case-dashboard/panels/analysis-panel/edit-analysis/landscape/landscape.component";
import { IFsProJobInformation } from "src/app/_interfaces/IFSProJobInformation";
import { EditRuleType } from "src/app/_interfaces/ILandscapeEdits";
import { HttpErrorResponse } from "@angular/common/http";
import { ILandscapeParameters } from "src/app/_interfaces/ILandscapeParameters";

@Injectable({
    providedIn: "root",
})
export class AnalysisFacadeService {
    // this is the Facade layer in the facade design pattern for the analysis feature area.
    // The responsibility of this layer is to expose streams of state and interface for all the related components in the Analysis feature.
    private _analysisIsCreating = new BehaviorSubject<boolean>(false);
    private _wfdssCase = new BehaviorSubject<WfdssCase>(null);
    private _analysisType = new BehaviorSubject<IUIAnalysisModel>(null);
    private _analysis = new BehaviorSubject<IAnalysis>(null);
    private _analyses = new BehaviorSubject<IAnalysis[]>([]);
    private _analysisSummary = new BehaviorSubject<IAnalysisSummary>(null);
    private _landscapeValid = new BehaviorSubject<{isValid: boolean, name: string}>({isValid: false, name: "No Landscape Exists"})
    public analysisRestricted$ = this._analysis.asObservable().pipe(
        map((analysis)=>{
            if(!analysis){ return false}
            if(analysis.status == "Completed For Operational Use" || analysis.status == "Completed For Strategic Use"){
                return true
            }
            return false
        })
    )
    private _startDateChangedFromPreviouslySavedValue = new Subject<void>()
    /** @description an emission on this stream indicates that an existing(and previously saved) analysis has been saved again with a new start date. Use for feature areas with start date sensitive logic eg. FsPro weather. Does not emit on first save */
    public startDateChangedFromPreviouslySavedValue$ = this._startDateChangedFromPreviouslySavedValue.asObservable()
    private _canCreateLandscapeFile = new BehaviorSubject<boolean>(false);
    private _fsProJobStatusAndAnalysisId = new BehaviorSubject<{job: IFsProJobInformation, analysis: IAnalysis}>(null)
    /** if we need to make the locks more complex we can do it here */
    private _enableDisableFeatureArea = new BehaviorSubject<boolean>(false);
    /**
     * @description every time a PUT or a GET of landscape edit rules occurs, this subject updates with the latest edit rules.*/
    private _latestLandscapeEditRuleValue = new BehaviorSubject<EditRuleType[]>(null);
    private _onSubmitLandscapeEditRuleMasks = new Subject<string[]>()
    /**
     * @description this observable fires when a user submits a landscape job request(successfully), and emits the masks used by the edit rules.*/
    public onSubmitLandscapeEditRuleMasks$ = this._onSubmitLandscapeEditRuleMasks.asObservable()
    wfdssCase$ = this._analysisIsCreating.asObservable();
    analysisType$ = this._analysisType.asObservable(); // TODO: Not used anywhere. So might need removed.
    analysis$ = this._analysis.asObservable().pipe(
        distinctUntilChanged(), 
    );
    analyses$ = this._analyses.asObservable();
    analysisSummary$ = this._analysisSummary.asObservable();
    canCreateLandscapeFile$ = this._canCreateLandscapeFile.asObservable();
    /**
        @description whether the analysis is in a "read-only" mode. Analyses are in read-only mode when they are at or after "Processing" status 
        @example Analysis statuses that would set restricedEditing to true:  "Processing" ,"Terminated","Failed","Review","Completed For Operational Use","Completed For Strategic Use","Reference","Calibration"
        @example Analysis statuses that would set restricedEditing to false: "Assigned", "Requested"
        @note A null analysis or null status would set restrictedEditing to true by default.
     * */
    enableDisableFeatureArea$ = this._enableDisableFeatureArea.asObservable()
    fsProJobStatusAndAnalysisId$ = this._fsProJobStatusAndAnalysisId.pipe(
        filter((jobAndAnalysis) => {
            if(!jobAndAnalysis) return false
            if(!jobAndAnalysis.analysis  || !jobAndAnalysis.job) return false
            return true
        })
    )
    canViewOrEditAnalysisInd: 'view' | 'edit';
    public landscapeIsValid$ = this._landscapeValid.asObservable()
    constructor(private dataSvc: AnalysisDataService, private irwinSearchService: IrwinSearchService) {
        // this breaks the rules, but has been vetted for memory leaks and is OK
        this._analysis.asObservable().pipe(
            tap((analysis)=>{
                if(analysis?.status != null && analysis?.status != undefined){
                    this.setEnableDisableFeatureArea(analysis)
                }
             })
        ).subscribe()
    }
    getAnalysisIsCreating() {
        return this._analysisIsCreating.asObservable();
    }
    setAnalysisIsCreating(isCreating: boolean) {
        this.refreshExistingAnalyses();
        if (isCreating == false) {
            this.setAnalysis(null);
        }
        this._analysisIsCreating.next(isCreating);
    }
    getLandfireProductValues() {
        return this.dataSvc.getLandfireProductValues();
    }
    getAnalysisType(): Observable<IUIAnalysisModel> {
        return this.analysisType$;
    }
    updateAnalysisType(analysisType: IUIAnalysisModel) {
        this._analysisType.next(analysisType);
    }
    /**@description sets the analysis in the facade service. Can be updated from a FormGroup, or from an API call*/
    setAnalysis(analysis: IAnalysis) {
        this._analysis.next(analysis);
        if (!analysis) {
            return;
        }
        const modelType = AnalysisModelTypes.find((a) => {
            return a.modelType === analysis.modelType;
        });
        this.updateAnalysisType(modelType);
    }
    saveAnalysis(analysis: IAnalysis) {
        const newAnalysis = JSON.parse(JSON.stringify(analysis));
        if(!newAnalysis.isIncludedInDecision) {
            newAnalysis.isIncludedInDecision = false; // prevent null being sent to the backend
        }
        if(this._analysis.value?.startDate){
            if(this._analysis.value.startDate != analysis?.startDate?.valueOf()){
                this._startDateChangedFromPreviouslySavedValue.next()
            }
        }
        return this.dataSvc.putAnalysisParameters(newAnalysis).pipe(
            tap((analysis: IAnalysis) => {
                this.setAnalysis(analysis);
            })
        );
    }
    saveAnalysisSummary(analysisSummary: IAnalysisSummary) {
        return this.dataSvc.saveAnalysisSummary(analysisSummary).pipe(take(1))
        .subscribe((analysisSummary: IAnalysisSummary) => {
            this._analysisSummary.next(analysisSummary);
        });
    }
    saveAnalysisRequest(analysis: IAnalysis) {
        return this.dataSvc.putSaveAnalysisRequestParameters(analysis);
    }
    saveAnalysisCopy(newTitle: string, analysisIdToCopy: number, modelType: UIAnalysisModelType){
        if(modelType === "FSPro"){
        return this.dataSvc.saveFsproAnalysisCopy(newTitle, analysisIdToCopy).pipe(
            take(1),
            tap(()=> this.refreshExistingAnalyses())
            )
        }
    }
    acceptAnalysisRequest(analysis: IAnalysis) {
        return this.dataSvc.getSaveAcceptAnalysis(analysis).pipe(
            tap((acceptedAnalysisResult) => {
                this.setAnalysis(acceptedAnalysisResult);
            })
        );
    }
    setWfdssCase(wfdssCase: WfdssCase) {
        this._wfdssCase.next(wfdssCase);
    }
    getWfdssCase() {
        return this.irwinSearchService.wfdssCase;
    }

    setEditAnalysisDefaults(rafg: UntypedFormGroup) {
        rafg.get("fuelModelSelector").setValue("40FM");
        // rafg.get("dataSource").setValue("LF 2001"); (default value is already set as first value in the list that should be the latest LF value)
        rafg.get("dataSource").updateValueAndValidity();

        let defaultResolution = 90; // Applicabe for 'FsPro' analysis type
        const analysisModelType = this._analysisType.getValue()?.modelType;
        switch(analysisModelType?.toLowerCase()) {
            case 'farsite':
                defaultResolution = 60;
                break;
            case 'mtt':
                defaultResolution = 30;
                    break;
            // TODO: Add cases for other two types: MTT & FlamMap
        }
        rafg.get("resolution").setValue(defaultResolution);
    }
    /** @description fetches the saved landscape params for the provided analysis. Only one landscape can exist per analysis*/
    getSavedLandscapeParameters(analysis: IAnalysis) {
        return this.dataSvc.getLandscapeParameters(analysis);
    }
    setLandscapeIsValid(landscapeValid: {isValid: boolean, name: string}){
        this._landscapeValid.next(landscapeValid)
    }
    /** @description fetches all analyses for a given wfdssCase*/
    refreshExistingAnalyses() {
        if(!this._wfdssCase.value){
            return
        }
        this.dataSvc
            .getExistingAnalysisForWfdssCaseId(this._wfdssCase.value)
            .pipe(take(1))
            .subscribe((analyes: IAnalysis[]) => {
                this._analyses.next(analyes);
            });
    }

    getAnalysisSummary() {
        if(!this._wfdssCase.value){
            return
        }
        this.dataSvc
            .getAnalysisSummary(this._wfdssCase.value)
            .pipe(
                take(1)             
            )
            .subscribe((analysisSummmary: IAnalysisSummary) => {
                this._analysisSummary.next(analysisSummmary);
            }, ((error: HttpErrorResponse) => {
                console.log(error)
                if(error.status == 404) {
                    this._analysisSummary.next(null)
                }
            }));
    }

    saveReadyForPublication(isRFP: boolean) {
        if(!this._wfdssCase.value){
            return
        }
        this.dataSvc
            .saveReadyForPublication(this._wfdssCase.value.wfdssCaseId, isRFP)
            .pipe(
                take(1)             
            )
            .subscribe((analysisSummmary: IAnalysisSummary) => {
                this._analysisSummary.next(analysisSummmary);
            });
    }

    getAnalysisSummarySubj() {
        return this._analysisSummary;
    }


    refreshAnalysisModelStatus(): void {
        this.dataSvc
            .getAnalysis(this._analysis.value.analysisId)
            .pipe(take(1))
            .subscribe((analysis: IAnalysis) => {
                this._analysis.next(analysis)
                this.setEnableDisableFeatureArea(analysis);
            });

    }

    setEnableDisableFeatureArea(analysis?: IAnalysis) {
        if(!analysis?.status){
            this._enableDisableFeatureArea.next(false)
        } else {
            this._enableDisableFeatureArea.next(
                analysis.status === "Processing" ||
                analysis.status === "Terminated" ||
                analysis.status === "Failed" ||
                analysis.status === "Review" ||
                analysis.status === "Completed For Operational Use" ||
                analysis.status === "Completed For Strategic Use" ||
                analysis.status === "Reference" ||
                analysis.status === "Calibration"
            );
        }
    }

    saveLandscape(landscapeParams: ILandscapeParameters, analysis: IAnalysis) {
        return this.dataSvc.saveLandscapeParameters(landscapeParams, analysis);
    }
    generateLandscapeName(landscapeParams: ILandscapeJobInformation) {
        const monthDayYear = moment(landscapeParams.successTime).format("MMDDYYYY");
        const time = moment(landscapeParams.successTime).format("HHmm");
        // Note: editRules below is a json string and not actual JS array. So, '[]' json string represents empty array.
        //       Length of this string is 2.
        const hasEditRoles = landscapeParams?.submittedLandscapeParameters?.editRules?.length>2 ? true: false; 

        return `${landscapeParams.submittedLandscapeParameters.extentName?.replace(/\s/g, "")}_${landscapeParams.submittedLandscapeParameters.resolution}M_${
            landscapeParams.submittedLandscapeParameters.productName
        }_${landscapeParams.submittedLandscapeParameters.fuelModelSelector}_${hasEditRoles ? "WithRules" : "NoRules"}_${monthDayYear}_${time}`;
    }
    /**
     * @description For successful & complete LS jobs. Job inputs(chosen by user on submission) are made human readable.
     * @param jobInfo
     * @returns a summary object populated with user readable properties.
     */
    generateCompletedLSSummary(jobInfo: ILandscapeJobInformation): ICompleteLSJobSummary | null{
        const targetTz: string = moment.tz.guess();
        const successDateTimeString: string = moment(jobInfo.successTime).tz(targetTz).format("MM/DD/YYYY HH:mm z");
        jobInfo.submittedLandscapeParameters.extentName = jobInfo.submittedLandscapeParameters.extentName.replace(/\s/g, "")
        var summaryParams = jobInfo.submittedLandscapeParameters
        delete summaryParams.lowerLeftLatitude
        delete summaryParams.lowerLeftLongitude
        delete summaryParams.upperRightLatitude
        delete summaryParams.upperRightLongitude
        var successJobSummary = {
            jobSubmissionParameters: summaryParams,
            successDateTime: successDateTimeString,
        }
        return successJobSummary
    }
    /**
     *
     * @param analysis the landscape will be saved to.
     * @param landscapeParams used as inputs for model generation. Extent + options.
     * @returns
     */
    submitLSJobRequest(analysis: IAnalysis, landscapeParams: ILandscapeParameters) {
        // this._landscapeSubmission.next()
        return this.dataSvc.submitLandscapeJobRequest(analysis, landscapeParams).pipe(
            tap((analy)=>{
                let masks: string[] = []
                // this._lsEditSaveEvent.
                this._latestLandscapeEditRuleValue.value?.forEach((rule)=>{
                    if(rule?.mask){
                        masks.push(rule.mask)
                    }
                })
                masks.length ? this._onSubmitLandscapeEditRuleMasks.next(masks) : this._onSubmitLandscapeEditRuleMasks.next(null)
            })
        );
    }
    /**
     * @description Retrieves information and status about the landscape which has been submitted to LFPS.*/
    getLandfireJobStatus(analysis: IAnalysis) {
        return this.dataSvc.getLandfireJobInformation(analysis);
    }

    runFsProJob(analysis: IAnalysis): Observable<any> {
        return this.dataSvc.runFsProJob(analysis);
    }
    getFsProJobStatus(analysis: IAnalysis): Observable<any> {
        return this.dataSvc.getFsProJobInformation(analysis).pipe(
            tap((fsprojob: IFsProJobInformation)=>{
                this._fsProJobStatusAndAnalysisId.next({
                    job:fsprojob,
                    analysis:analysis
                })
            })
        );
    }
    terminateFsProJob(analysis: IAnalysis): Observable<any> {
        return this.dataSvc.terminateFsProJob(analysis);
    }

    getFsProModelOutput(analysis: IAnalysis, type: EFsProModelOutputType){
        return this.dataSvc.getFsProJobOutputZip(analysis, type)
    }
    getFsProModeltxt(analysis: IAnalysis, type: EFsProModelOutputType){
        return this.dataSvc.getFsProJobOutputTxt(analysis, type)
    }

    getLandscapeZip(analysisId: number){
        return this.dataSvc.getLandscapeZip(analysisId)
    }

    saveLandscapeEditRules(rules: EditRuleType[]){
        let upsertRules =  this.LSEditTransformFormGroupValuesToUpsertValues(rules)// transform the formgroup values on the way in to for the backend
        return this.dataSvc.putSaveLandscapeEditRules(this._analysis.value, upsertRules).pipe(
            tap(()=>{
                this._latestLandscapeEditRuleValue.next(rules) // keep this subject populated  with the latest edit rules
            }),
            map((rules)=>{
                return this.LSEditRulesTransformSavedValueToFormgroupValue(rules) // and transform the backend values on the way back for the formgroup 
            })
        )
    }
    getLandscapeEditRules(){
        return this.dataSvc.getLandscapeEditRules(this._analysis.value).pipe(           
            tap((rules)=>{ this._latestLandscapeEditRuleValue.next(rules)}), // keep this subject populated  with the latest edit rules
            map((rules)=>{
                return this.LSEditRulesTransformSavedValueToFormgroupValue(rules) // transform the backend values for the formgroup
            })
        )
    }

    downloadFailedRunInputFile(){
        return this.dataSvc.getFailedFsProInputFile(this._analysis.value)
    }

    /**
     * Transforms value received from Land Fire Product Service(LFPS) to user input format. (backend communicates with LFPS)
     */
    private LSEditRulesTransformSavedValueToFormgroupValue(rules: EditRuleType[]): EditRuleType[]{
            return rules.map((rule)=>{
                 rule.modifier.map((attributeModifer)=>{
                     // Note: Value for 'Multiply By (mb)' needs no transformation. 
                     if(attributeModifer.operator === 'mb') {
                       return attributeModifer;
                     }

                     switch(attributeModifer.attribute){
                         case"ch":
                             attributeModifer.value = attributeModifer.value as number / 10
                             break;
                         case"cbd":
                             attributeModifer.value = attributeModifer.value as number / 100
                             break;
                         case"cbh":
                             attributeModifer.value = attributeModifer.value as number / 10
                             break;
                     }
                     return attributeModifer
                 })
                 rule.condition.map((conditionSelector)=>{
                     switch(conditionSelector.attribute){
                         case"ch":
                             conditionSelector.value = conditionSelector.value as number / 10
                             break;
                         case"cbd":
                             conditionSelector.value = conditionSelector.value as number / 100
                             break;
                         case"cbh":
                             conditionSelector.value = conditionSelector.value as number / 10
                             break;
                     }
                     return conditionSelector
                 })
                 return rule
             })
    }

    private LSEditTransformFormGroupValuesToUpsertValues(rules: EditRuleType[]):EditRuleType[]{
        return rules.map((rule)=>{
            rule.modifier.map((attributeModifer)=>{
                // Note: Value for 'Multiply By (mb)' needs no transformation. 
                if(attributeModifer.operator === 'mb') {
                    return attributeModifer;
                }
                switch(attributeModifer.attribute){
                    case"ch":
                        attributeModifer.value = attributeModifer.value as number * 10
                        break;
                    case"cbd":                        
                        attributeModifer.value = attributeModifer.value as number * 100                        
                        break;
                    case"cbh":
                        attributeModifer.value = attributeModifer.value as number * 10
                        break;
                }
                return attributeModifer
            })
            rule.condition.map((conditionSelector)=>{
                switch(conditionSelector.attribute){
                    case"ch":
                        conditionSelector.value = conditionSelector.value as number * 10
                        break;
                    case"cbd":
                        conditionSelector.value = conditionSelector.value as number * 100
                        break;
                    case"cbh":
                        conditionSelector.value = conditionSelector.value as number * 10
                        break;
                }
                return conditionSelector
            })
            return rule
        })

    }

    downloadModelOutput(type, analysis){
        if(type === "output-zip"){
            this.getFsProModelOutput(analysis, type).subscribe(
                (model: any) => {
                    this.handleDownload(model, type, analysis);
                }
            );
        } else if (type === "daily-acres-txt" || type === "event-coverage-txt"){
            this.getFsProModeltxt(analysis, type).subscribe((textString: string)=>{
                this.handleDownload(textString, type, analysis)

            })
        } else if (type === "landscape-zip") {
            this.getLandscapeZip(analysis.analysisId).subscribe(
                (model: any) => {
                    this.handleDownload(model, type, analysis);
                }
            );
        }
    }

    handleDownload(model: Blob | string, type: EFsProModelOutputType, analysis){
        const dummyElement = document.createElement('a');
        const blob = new Blob([model]);
        const url = window.URL.createObjectURL(blob);
    
        dummyElement.href = url;

        if(type === "event-coverage-txt" || type === "daily-acres-txt"){
            dummyElement.download = `${analysis.title} ${type.substring(0, type.length-4)}.txt` 
        } 
        else if (["output-zip", "landscape-zip"].includes(type)){
            dummyElement.download = `${analysis.title} ${type.substring(0, type.length-4)}.zip`
        }
        dummyElement.click();
        window.URL.revokeObjectURL(url);
        dummyElement.remove();
    }
}
